import React from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { REVENUE_TYPES } from '../../../services/investmentPlanningService';
import { getDefaultGrowthPercentDisplay } from '../../../services/planningTax/revenueSchedule';

export const REVENUE_FORM_EMPTY = {
  name: '',
  type: '',
  entityId: '',
  monthlyAmount: '',
  isGross: true,
  notes: '',
  linkedAssetId: '',
  reinvest: false,
  reinvestTargetId: '',
  sourceCorpEntityId: '',
  dividendClass: 'non_eligible',
  dividendEligible: false,
  annualGrowthPercent: '',
  adjustments: [],
};

const DIVIDEND_CLASS_OPTIONS = [
  { value: 'non_eligible', label: 'Ordinaire' },
  { value: 'eligible', label: 'Admissible' },
  { value: 'capital', label: 'Capital (reçu corp.)' },
];

function getRevenuePlaceholder(type) {
  const p = {
    salaire: 'Ex: Salaire emploi principal',
    autre: 'Ex: Revenu de consultation',
  };
  return p[type] || 'Nom du revenu';
}

const RevenueLineForm = ({
  form,
  setForm,
  editingId,
  entities = [],
  assets = [],
  formatCurrency,
  typeOptions = REVENUE_TYPES,
  showGrowth = false,
  showAdjustments = false,
  showDividendFields = true,
  showCorpSelector = false,
  holderEntityFilter = 'all',
  currentYear,
  horizonYears = 25,
  onSave,
  onCancel,
  onTypeChange,
  onAssetLink,
}) => {
  const holderOptions = entities
    .filter((e) => holderEntityFilter === 'all' || e.type === holderEntityFilter)
    .map((e) => ({ value: e.id, label: e.name }));
  const corpOptions = entities.filter((e) => e.type === 'corporation').map((e) => ({ value: e.id, label: e.name }));
  const hasCorps = corpOptions.length > 0;
  const showCorpField = showCorpSelector && hasCorps && !!form.type;

  const revenueAssetOptions = assets
    .filter((a) => !form.sourceCorpEntityId || a.entityId === form.sourceCorpEntityId)
    .map((a) => ({
      value: a.id,
      label: `${a.name} (${a.type === 'immobilier' ? 'Immobilier' : (a.type || '').toUpperCase()})`,
    }));

  const holderLabel = holderEntityFilter === 'personnel' ? 'Bénéficiaire (personne)' : 'Détenteur';

  const handleCorpSelect = (corpId) => {
    setForm((prev) => {
      const next = { ...prev, sourceCorpEntityId: corpId };
      if (corpId && prev.linkedAssetId) {
        const asset = assets.find((a) => a.id === prev.linkedAssetId);
        if (asset && asset.entityId !== corpId) {
          next.linkedAssetId = '';
        }
      }
      return next;
    });
  };
  const reinvestTargetOptions = assets.filter((a) => a.type !== 'immobilier').map((a) => ({
    value: a.id,
    label: `${a.name} (${formatCurrency(a.currentBalance)})`,
  }));

  const needsAssetLink = ['revenu_immeuble', 'revenu_placement', 'dividende'].includes(form.type);
  const isSalary = form.type === 'salaire';
  const defaultGrowth = form.type ? getDefaultGrowthPercentDisplay({ type: form.type }) : '';

  const renderTypeFields = () => {
    if (!form.type) return null;

    return (
      <>
        {needsAssetLink && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Actif source du revenu"
              value={form.linkedAssetId}
              onChange={onAssetLink}
              options={revenueAssetOptions}
              placeholder={assets.length === 0 ? "Ajoutez d'abord un actif..." : "Sélectionner l'actif..."}
              data-testid="revenue-asset-select"
            />
            <Select label={holderLabel} value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={holderOptions} placeholder="Sélectionner..." required />
          </div>
        )}

        {!needsAssetLink && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={getRevenuePlaceholder(form.type)} required data-testid="revenue-name-input" />
            <Select label={holderLabel} value={form.entityId} onChange={(v) => setForm({ ...form, entityId: v })} options={holderOptions} placeholder="Sélectionner..." required />
          </div>
        )}

        {showCorpField && (
          <Select
            label="Société (Inc.)"
            value={form.sourceCorpEntityId}
            onChange={handleCorpSelect}
            options={[{ value: '', label: '— Aucune —' }, ...corpOptions]}
            placeholder="Sélectionner une Inc..."
            data-testid="revenue-source-corp"
          />
        )}

        {needsAssetLink && form.linkedAssetId && (
          <Input label="Nom du revenu" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="revenue-name-input" />
        )}

        {showDividendFields && form.type === 'dividende' && (
          <div className="border border-primary/20 rounded-lg p-3 bg-primary/5">
            <Select label="Classe de dividende" value={form.dividendClass} onChange={(v) => setForm({ ...form, dividendClass: v, dividendEligible: v === 'eligible' })} options={DIVIDEND_CLASS_OPTIONS} />
          </div>
        )}

        <div className={`grid grid-cols-1 gap-3 ${showGrowth ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <Input label="Montant mensuel ($)" type="number" value={form.monthlyAmount} onChange={(e) => setForm({ ...form, monthlyAmount: e.target.value })} placeholder={isSalary ? '5 000' : '2 500'} data-testid="revenue-monthly-input" />
          {showGrowth && (
            <Input
              label="Croissance annuelle (%)"
              type="number"
              step="0.1"
              value={form.annualGrowthPercent}
              onChange={(e) => setForm({ ...form, annualGrowthPercent: e.target.value })}
              placeholder={String(defaultGrowth)}
              data-testid="revenue-growth-input"
            />
          )}
          <div className="flex items-end gap-2 pb-1">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input type="checkbox" checked={form.isGross} onChange={(e) => setForm({ ...form, isGross: e.target.checked })} className="h-4 w-4 rounded border-input" />
              Montant brut
            </label>
          </div>
        </div>

        <div className="border-t border-border pt-3 mt-1">
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer mb-2" data-testid="reinvest-toggle">
            <input
              type="checkbox"
              checked={form.reinvest}
              onChange={(e) => setForm({ ...form, reinvest: e.target.checked, reinvestTargetId: e.target.checked ? form.reinvestTargetId : '' })}
              className="h-4 w-4 rounded border-input"
            />
            <Icon name="RefreshCw" size={14} className="text-primary" />
            <span>Réinvestir ce revenu</span>
          </label>
          {form.reinvest && (
            <Select
              label="Compte de réinvestissement"
              value={form.reinvestTargetId}
              onChange={(v) => setForm({ ...form, reinvestTargetId: v })}
              options={reinvestTargetOptions}
              placeholder={reinvestTargetOptions.length === 0 ? "Ajoutez d'abord un compte d'épargne..." : 'Sélectionner le compte cible...'}
              data-testid="reinvest-target-select"
            />
          )}
        </div>
      </>
    );
  };

  return (
    <div className="bg-muted rounded-lg p-4 mb-4 space-y-3" data-testid="revenue-form">
      <Select
        label="Type de revenu"
        value={form.type}
        onChange={onTypeChange}
        options={typeOptions}
        placeholder="Choisir le type de revenu..."
        required
        data-testid="revenue-type-select"
      />
      {renderTypeFields()}
      {showAdjustments && form.type && (
        <RevenueAdjustmentsEditor
          adjustments={form.adjustments || []}
          onChange={(adjustments) => setForm({ ...form, adjustments })}
          currentYear={currentYear}
          horizonYears={horizonYears}
        />
      )}
      {form.type && (
        <>
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optionnel" />
          <div className="flex gap-2">
            <Button size="sm" onClick={onSave} data-testid="revenue-save-btn">{editingId ? 'Mettre à jour' : 'Ajouter'}</Button>
            <Button size="sm" variant="outline" onClick={onCancel} data-testid="revenue-cancel-btn">Annuler</Button>
          </div>
        </>
      )}
    </div>
  );
};

export function RevenueAdjustmentsEditor({ adjustments = [], onChange, currentYear, horizonYears = 25 }) {
  const maxYear = currentYear + horizonYears;

  const updateAdj = (index, patch) => {
    const next = adjustments.map((a, i) => (i === index ? { ...a, ...patch } : a));
    onChange(next);
  };

  const addAdj = () => {
    onChange([
      ...adjustments,
      {
        id: `adj-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        effectiveYear: currentYear + 5,
        monthlyAmount: 0,
        label: '',
      },
    ]);
  };

  const removeAdj = (index) => {
    onChange(adjustments.filter((_, i) => i !== index));
  };

  return (
    <div className="border border-border rounded-lg p-3 space-y-3" data-testid="revenue-adjustments-panel">
      <p className="text-sm font-medium text-foreground">Ajustements planifiés</p>
      <p className="text-xs text-muted-foreground">
        Remplace le montant mensuel à partir de l&apos;année indiquée (ex. retraite). Années valides : {currentYear}–{maxYear}.
      </p>
      {adjustments.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucun ajustement. Ajoutez une étape si le revenu change à une date précise.</p>
      ) : (
        adjustments.map((adj, index) => (
          <div key={adj.id || index} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid={`revenue-adjustment-row-${index}`}>
            <Input label="Année" type="number" value={adj.effectiveYear} onChange={(e) => updateAdj(index, { effectiveYear: parseInt(e.target.value, 10) || currentYear })} data-testid="adjustment-year-input" />
            <Input label="Nouveau mensuel ($)" type="number" value={adj.monthlyAmount} onChange={(e) => updateAdj(index, { monthlyAmount: parseFloat(e.target.value) || 0 })} data-testid="adjustment-amount-input" />
            <Input label="Libellé" value={adj.label || ''} onChange={(e) => updateAdj(index, { label: e.target.value })} placeholder="Retraite" data-testid="adjustment-label-input" />
            <Button size="sm" variant="ghost" iconName="Trash2" onClick={() => removeAdj(index)} style={{ color: 'var(--color-destructive)' }} />
          </div>
        ))
      )}
      <Button size="sm" variant="outline" iconName="Plus" onClick={addAdj} data-testid="add-adjustment-btn">
        Ajouter un ajustement
      </Button>
    </div>
  );
}

export default RevenueLineForm;
